import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CreditCard, Loader2, Lock, ShieldCheck, Check } from 'lucide-react';
import { trackPixel } from '@/lib/metaPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const empty = { name: '', email: '', phone: '', city: '' };

const BundleCheckoutModal = ({ open, onOpenChange, bundle }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [waitlisted, setWaitlisted] = useState(null); // { city, message } | null

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = (val) => {
    onOpenChange(val);
    if (!val) setTimeout(() => { setForm(empty); setWaitlisted(null); }, 250);
  };

  const canSubmit = form.name.trim() && form.email.trim() && form.phone.trim() && form.city.trim();

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please fill all fields');
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/bundle/create-order`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        bundle_size: bundle.size,
      });

      // Service area gate: outside Delhi NCR → show friendly waitlist message
      if (res.data?.waitlisted) {
        setWaitlisted({ city: form.city.trim(), message: res.data.message });
        trackPixel('Lead', { content_name: `Bundle Waitlist · ${bundle.label}`, content_category: 'YouTuberBundle', city: form.city.trim() });
        setLoading(false);
        return;
      }

      const { order_id, payment_session_id } = res.data;

      // Pixel: user is about to be redirected to Cashfree
      trackPixel('InitiateCheckout', {
        content_name: bundle.label,
        content_category: 'YouTuberBundle',
        content_ids: [`bundle_${bundle.size}`],
        value: bundle.amount,
        currency: 'INR',
        num_items: bundle.size,
      });

      const { load } = await import('@cashfreepayments/cashfree-js');
      const cashfree = await load({ mode: 'production' });

      cashfree.checkout({
        paymentSessionId: payment_session_id,
        returnUrl: `${BACKEND_URL}/api/payment/return?order_id=${order_id}`,
        redirectTarget: '_self',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
      toast.error(msg);
      setLoading(false);
    }
  };

  if (!bundle) return null;

  const saved = bundle.original - bundle.amount;
  const perVisit = Math.round(bundle.amount / bundle.size);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bundle-modal" data-testid="bundle-checkout-modal">
        <VisuallyHidden.Root>
          <DialogTitle>Buy {bundle.label}</DialogTitle>
          <DialogDescription>Enter your details to proceed to secure Cashfree checkout.</DialogDescription>
        </VisuallyHidden.Root>

        <div className="bundle-modal-header">
          <div className="bundle-modal-icon"><CreditCard size={22} /></div>
          <h3>Buy {bundle.label}</h3>
          <p>Secure checkout via Cashfree</p>
        </div>

        {waitlisted ? (
          <div data-testid="bundle-waitlist-message" style={{ padding: '18px 4px 6px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🌱</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 20, color: '#0f172a' }}>Coming soon to {waitlisted.city}!</h3>
            <p style={{ margin: '0 0 14px', color: '#4b5563', fontSize: 14, lineHeight: 1.55 }}>{waitlisted.message}</p>
            <p style={{ margin: '0 0 18px', color: '#6b7280', fontSize: 13 }}>We've saved your details and will email you the moment we launch in your city.</p>
            <Button onClick={() => close(false)} className="bundle-checkout-btn" data-testid="bundle-waitlist-close">Got it</Button>
          </div>
        ) : (<>
        <div className="bundle-modal-summary">
          <div className="bundle-summary-row">
            <span>{bundle.size} home visits</span>
            <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>&#8377;{bundle.original.toLocaleString('en-IN')}</span>
          </div>
          <div className="bundle-summary-row" style={{ color: '#16a34a' }}>
            <span>Early Bird discount ({bundle.discount}%)</span>
            <strong>-&#8377;{saved.toLocaleString('en-IN')}</strong>
          </div>
          <div className="bundle-summary-row bundle-summary-total">
            <span>You pay today (incl. 18% GST)</span>
            <strong>&#8377;{bundle.amount.toLocaleString('en-IN')}</strong>
          </div>
          <div className="bundle-summary-per">≈ &#8377;{perVisit.toLocaleString('en-IN')} per visit</div>
        </div>

        <form onSubmit={handleCheckout} className="bundle-form">
          <div className="bundle-field">
            <label>Full Name *</label>
            <Input
              data-testid="bundle-name-input"
              placeholder="e.g. Suresh Sharma"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              autoFocus
              disabled={loading}
            />
          </div>
          <div className="bundle-field">
            <label>Email *</label>
            <Input
              data-testid="bundle-email-input"
              type="email"
              placeholder="your@email.com"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              disabled={loading}
            />
            <span className="bundle-field-hint">Receipt will be sent here</span>
          </div>
          <div className="bundle-field">
            <label>Phone *</label>
            <Input
              data-testid="bundle-phone-input"
              type="tel"
              placeholder="98765 43210"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="bundle-field">
            <label>City *</label>
            <Input
              data-testid="bundle-city-input"
              placeholder="e.g. New Delhi, Gurugram, Noida"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              disabled={loading}
            />
            <span className="bundle-field-hint">Currently serving Delhi NCR</span>
          </div>

          <Button
            type="submit"
            className="bundle-checkout-btn"
            disabled={loading || !canSubmit}
            data-testid="bundle-checkout-btn"
          >
            {loading
              ? <><Loader2 size={16} className="adm-spin" /> Redirecting to Cashfree...</>
              : <><Lock size={15} /> Pay &#8377;{bundle.amount.toLocaleString('en-IN')} securely</>}
          </Button>
          
          <div className="bundle-trust">
            <span><ShieldCheck size={13} /> Secure payment</span>
            <span><Check size={13} /> Instant email receipt</span>
            <span><Check size={13} /> {bundle.size} visits, 12 months</span>
          </div>

          <div data-testid="bundle-billed-by" style={{ marginTop: 10, fontSize: 11, color: '#6b7280', textAlign: 'center', lineHeight: 1.5 }}>
            Billed by <strong>GiantTreeTechnology Private Limited</strong><br />
            GSTIN: 07AAICG7122N1ZA · All prices inclusive of 18% GST
          </div>

          <button type="button" className="bundle-cancel-btn" onClick={() => close(false)} disabled={loading} data-testid="bundle-modal-cancel">
            Cancel
          </button>
        </form>
        </>)}
      </DialogContent>
    </Dialog>
  );
};

export default BundleCheckoutModal;
